/**
 * Shared helpers for the Engagement / Resell card UI.
 *
 * IMPORTANT: This file contains UI-only helpers. No API calls, no backend
 * logic. All inputs come from the existing engagement/resell deal payload
 * returned by `fetchEngagementDealsAndResell`.
 */

import {
  FaDesktop,
  FaCheckCircle,
  FaRegCalendarAlt,
  FaUserFriends,
  FaBuilding,
  FaTimesCircle,
} from "react-icons/fa";

/* ----------------------------- Status colors ----------------------------- */

export const STATUS_COLORS = {
  blue: "#3b82f6",
  yellow: "#facc15",
  green: "#22c55e",
  red: "#ef4444",
  orange: "#f97316",
  gray: "#6b7280",
  grey: "#6b7280",
};

/* ----------------------------- Normalisers ------------------------------- */

const normalize = (s) =>
  (s || "")
    .toString()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

/* ----------------------------- Box defs ---------------------------------- */

export const ENGAGEMENT_BOXES = [
  {
    key: "deliverables",
    label: "Deliverables",
    icon: FaDesktop,
    accent: "#7269ef",
    matches: ["deliverables", "deliverable"],
    unit: "items",
  },
  {
    key: "engagement_core",
    label: "Engagement core",
    icon: FaCheckCircle,
    accent: "#22c55e",
    matches: ["engagement core", "engagement"],
    unit: "fields",
  },
  {
    key: "fees_billing",
    label: "Fees & billing",
    icon: FaRegCalendarAlt,
    accent: "#3b82f6",
    matches: ["fees and billing", "fees billing", "billing", "fees", "payment", "pricing"],
    unit: "items",
  },
  {
    key: "parties",
    label: "Parties",
    icon: FaUserFriends,
    accent: "#f97316",
    matches: ["parties", "party", "client", "vendor", "contacts"],
    unit: "parties",
  },
  {
    key: "termination",
    label: "Termination",
    icon: FaTimesCircle,
    accent: "#ef4444",
    matches: ["termination", "terminate", "exit", "cancellation"],
    unit: "clauses",
  },
  {
    key: "timeline",
    label: "Timeline",
    icon: FaBuilding,
    accent: "#a855f7",
    matches: ["timeline", "schedule", "duration"],
    unit: "fields",
  },
];

// Resell Deals tab uses the same boxes as Engagement *except* Termination,
// which only applies to engagement SOWs.
export const RESELL_BOXES = ENGAGEMENT_BOXES.filter((b) => b.key !== "termination");

/**
 * Match a category (from API) against a logical box definition.
 * Returns the matched category object or undefined.
 */
export const findCategory = (categories = [], box) => {
  const targets = box.matches.map(normalize);
  return categories.find((c) => targets.includes(normalize(c.category)));
};

/**
 * Build the metric text shown inside a box ("3 items", "6 months", ...).
 */
export const summarizeBox = (category, box) => {
  if (!category) return { value: "—", label: "" };

  const items = category.items || [];

  // Timeline: try to surface "Project Duration" or similar
  if (box.key === "timeline") {
    const durationItem = items.find((it) => /duration/i.test(it.name || ""));
    if (durationItem && durationItem.value) {
      return { value: String(durationItem.value), label: "" };
    }
    // Fallback: look for a "Status" field for resell timeline ("Complete")
    const statusItem = items.find((it) => /status/i.test(it.name || ""));
    if (statusItem && statusItem.value && box.unit === "fields" && items.length <= 2) {
      return { value: String(statusItem.value), label: "" };
    }
  }

  // Fees & billing: surface payment terms when present (e.g. "NET-30")
  if (box.key === "fees_billing") {
    const termsItem = items.find((it) => /payment\s*terms|net[\s-]?\d+|terms/i.test(it.name || ""));
    if (termsItem && termsItem.value && /^[A-Z0-9\-\s]+$/i.test(String(termsItem.value)) && String(termsItem.value).length <= 12) {
      return { value: String(termsItem.value).toUpperCase().replace(/\s+/g, "-"), label: "" };
    }
  }

  return { value: String(items.length), label: box.unit };
};

/* ----------------------------- PM Status parsing ------------------------- */

/**
 * Convert the parsed PM status (returned by getDealPmStatus) into raw date
 * (used for "Last Activity" aggregation) and other display info.
 */
export const extractPmStatsForSummary = (parsed) => {
  if (!parsed || typeof parsed !== "object") return {};
  let latestDateRaw = null;
  if (parsed.date) {
    const d = new Date(parsed.date);
    if (!isNaN(d.getTime())) latestDateRaw = d.toISOString();
  }
  return {
    totalEntries: parsed.totalEntries || (parsed.hasDate ? 1 : 0),
    latestDate: parsed.date || null,
    latestDateRaw,
    latestContent: parsed.content || null,
  };
};

/* ----------------------------- Misc ----------------------------- */

/**
 * Return initials for an email/full name (used in PMO avatar).
 */
export const getInitials = (text) => {
  if (!text) return "?";
  const cleaned = text.split("@")[0].replace(/[._-]+/g, " ").trim();
  const parts = cleaned.split(/\s+/);
  const a = parts[0]?.[0] || "";
  const b = parts[1]?.[0] || "";
  return (a + b).toUpperCase() || "?";
};

/**
 * Pick a stable colour for an avatar based on a string.
 */
export const colorFromString = (text) => {
  const palette = ["#7269ef", "#22c55e", "#f59e0b", "#3b82f6", "#ef4444", "#a855f7", "#0ea5e9"];
  let hash = 0;
  for (let i = 0; i < (text || "").length; i++) hash = (hash * 31 + text.charCodeAt(i)) | 0;
  return palette[Math.abs(hash) % palette.length];
};

/**
 * "SOW #5" style id from deal_id - use the last 1-3 digits or hash.
 * If the deal carries an explicit SOW number we'd use that; here we fall back
 * to the index (passed in) or a digest of deal_id.
 */
export const sowLabel = (deal, indexFallback) => {
  // Prefer a "SOW #" field surfaced in categories if present
  const cats = deal?.categories || [];
  for (const cat of cats) {
    for (const it of cat.items || []) {
      if (/sow\s*#?\s*number|sow\s*#?$/i.test(it.name || "") && it.value != null) {
        const num = String(it.value).replace(/[^0-9]/g, "");
        if (num) return `SOW #${num}`;
      }
    }
  }
  const dealId = deal?.deal_id?.toString() || "";
  if (dealId) {
    const tail = dealId.slice(-2).replace(/^0+/, "") || dealId.slice(-1);
    return `SOW #${tail}`;
  }
  return `SOW #${(indexFallback ?? 0) + 1}`;
};

/* ----------------------------- Change Requests --------------------------- */

/**
 * Normalise the Change Requests attached to an engagement deal.
 *
 * NOTE: The backend endpoint that links SOWs to their Change Requests (CRs) is
 * still being built (Gemini SOW/CR linking + HubSpot deal-ID requirement). This
 * helper reads whatever shape the API eventually returns WITHOUT assuming a
 * single field name, so the UI works the moment the data starts flowing.
 *
 * Accepted source fields (first non-empty wins):
 *   deal.change_requests | deal.changeRequests | deal.crs
 *
 * Each CR is normalised to:
 *   { id, name, dateRaw, date, categories, pm_status, is_original }
 *
 * Returns [] when no CR data is present (UI then shows an empty state).
 */
export const getChangeRequests = (deal) => {
  const raw =
    deal?.change_requests ?? deal?.changeRequests ?? deal?.crs ?? [];
  if (!Array.isArray(raw) || raw.length === 0) return [];

  const normalized = raw.map((cr, idx) => {
    const dateRaw = cr.close_date ?? cr.date ?? cr.cr_date ?? null;
    let dateLabel = null;
    if (dateRaw) {
      const d = new Date(dateRaw);
      dateLabel = isNaN(d.getTime())
        ? String(dateRaw)
        : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
    }
    return {
      id: cr.deal_id ?? cr.cr_id ?? cr.id ?? `cr-${idx}`,
      name: cr.deal_name ?? cr.cr_name ?? cr.name ?? `Change Request ${idx + 1}`,
      dateRaw,
      date: dateLabel,
      categories: cr.categories ?? [],
      pm_status: cr.pm_status ?? cr.summary ?? null,
      is_original: Boolean(cr.is_original ?? cr.original),
    };
  });

  // Order by date, newest first (CRs without a date sink to the bottom).
  normalized.sort((a, b) => {
    const ta = a.dateRaw ? new Date(a.dateRaw).getTime() : -Infinity;
    const tb = b.dateRaw ? new Date(b.dateRaw).getTime() : -Infinity;
    return tb - ta;
  });

  return normalized;
};

/* ----------------------------- Status badge ----------------------------- */

export const resolveStatusBadge = (deal) => {
  const raw = (deal.state || deal.status || "").toString();
  const lower = raw.toLowerCase();
  if (!raw) return { label: "—", color: "#9ca3af", bg: "rgba(156,163,175,0.15)" };
  if (lower.includes("complete") || lower.includes("done"))
    return { label: "Complete", color: "#16a34a", bg: "rgba(34,197,94,0.15)" };
  if (lower.includes("active") || lower.includes("in progress") || lower.includes("operational") || lower === "green" || lower === "blue" || lower === "yellow")
    return { label: "Active", color: "#16a34a", bg: "rgba(34,197,94,0.15)" };
  if (lower.includes("hold") || lower === "yellow")
    return { label: "On hold", color: "#b45309", bg: "rgba(245,158,11,0.15)" };
  if (lower === "red" || lower.includes("risk") || lower.includes("blocked"))
    return { label: "At risk", color: "#b91c1c", bg: "rgba(239,68,68,0.15)" };
  // Capitalise raw
  const formatted = raw.charAt(0).toUpperCase() + raw.slice(1);
  return { label: formatted, color: "#374151", bg: "rgba(55,65,81,0.1)" };
};
